/* iter55g23 — Synopsis Builder dialog.
 *
 * A structured 11-step synopsis (Save the Cat-style beat sheet) writers
 * can fill in manually or have Claude generate from their existing
 * manuscript. Enforces a 500–1500 word total.
 *
 * Invoked from the File menu → "Build synopsis…". Persists into
 * `project.synopsis` as a Markdown blob so it round-trips into the
 * existing Story-Bible / chapter-generation prompt pipeline.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Book, Loader2, Sparkles, Save, Wand2, BookOpen,
} from 'lucide-react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { projectsApi } from '@/lib/api';
import { usePW } from '@/lib/store';
import { useCapabilities } from '@/lib/useCapabilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 11-step structure — labels match the user's brief verbatim so their
// mental model is preserved exactly.
const STEPS = [
  { id: 'opening_image',     label: '1. Opening image',              hint: 'An image / setting / concept that sets the stage for the story.' },
  { id: 'protagonist_intro', label: '2. Protagonist intro',          hint: 'Who is the main character? Give 1-2 descriptive words and say what he/she wants.' },
  { id: 'inciting_incident', label: '3. Inciting incident',          hint: 'What event / decision / change prompts the MC to take initial action?' },
  { id: 'plot_point_1',      label: '4. Plot point 1',               hint: 'The first turning point. Once crossed, there\'s no going back.' },
  { id: 'conflicts',         label: '5. Conflicts & character encounters', hint: 'New life, new people, meets the antagonist/villain.' },
  { id: 'midpoint',          label: '6. Midpoint',                   hint: 'Middle turning point. A 180° change in direction, emotion, or anything.' },
  { id: 'imminent_win',      label: '7. Winning seems imminent, but…', hint: 'MC seems to have the upper hand — then the antagonist defeats them.' },
  { id: 'black_moment',      label: '8. Black moment',               hint: 'MC is lower than low. Fight through the darkness to find strength for the final battle.' },
  { id: 'climax',            label: '9. Climax',                     hint: 'The final blow-out between MC and antagonist.' },
  { id: 'resolution',        label: '10. Resolution',                hint: 'Does everyone live happily ever after? How are loose ends tied up?' },
  { id: 'final_image',       label: '11. Final image',               hint: 'The final image you want to leave the reader with.' },
];

const WORD_MIN = 500;
const WORD_MAX = 1500;

const countWords = (s) => String(s || '').split(/\s+/).filter(Boolean).length;

/** Compose the 11 fields into a single Markdown blob for `project.synopsis`. */
function toMarkdown(fields) {
  return STEPS
    .map((s) => {
      const text = (fields[s.id] || '').trim();
      if (!text) return '';
      return `## ${s.label}\n\n${text}\n`;
    })
    .filter(Boolean)
    .join('\n');
}

/** Parse an existing Markdown synopsis back into the 11-field shape. */
function fromMarkdown(md) {
  const out = {};
  const src = String(md || '');
  // Split on `## ` headings — each section is `## <label>\n\n<body>`.
  const parts = src.split(/\n(?=##\s)/);
  parts.forEach((p) => {
    const m = /^##\s+(\d+)\.\s*/.exec(p.trim());
    if (!m) return;
    const num = parseInt(m[1], 10);
    const step = STEPS[num - 1];
    if (!step) return;
    out[step.id] = p.replace(/^##.*\n\n?/, '').trim();
  });
  return out;
}

export default function SynopsisBuilderDialog({ open, onClose, project, onSaved }) {
  const [fields, setFields] = useState({});
  const [busy, setBusy]     = useState(false);
  const [saving, setSaving] = useState(false);
  // iter55g24 — Synopsis-to-chapter-transitions pipeline.
  const [transitions, setTransitions]     = useState([]);
  const [transitBusy, setTransitBusy]     = useState(false);
  const [transitError, setTransitError]   = useState('');
  const { rebuildNovel } = usePW();
  const { can: capsCan, upgradeCta } = useCapabilities();
  const canBulkDraft = capsCan('bulk_draft_novel');

  useEffect(() => {
    if (!open) return;
    setFields(fromMarkdown(project?.synopsis || ''));
    setTransitions([]);
    setTransitError('');
  }, [open, project?.synopsis]);

  const total = useMemo(
    () => Object.values(fields).reduce((s, v) => s + countWords(v), 0),
    [fields],
  );
  const inRange = total >= WORD_MIN && total <= WORD_MAX;

  const patch = (id, value) => setFields((prev) => ({ ...prev, [id]: value }));

  const build = async () => {
    if (!project?.id) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${project.id}/build-synopsis`, {
        min_words: WORD_MIN, max_words: WORD_MAX,
      }, { timeout: 120000 });
      const gen = r.data || {};
      // Backend returns {fields: {opening_image, protagonist_intro, …}}.
      setFields((prev) => ({ ...prev, ...(gen.fields || {}) }));
      toast.success('Claude built the synopsis from your manuscript.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d
        : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join(' · ')
        : (e?.message || 'Synopsis build failed.');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!project?.id) return;
    if (total > WORD_MAX) {
      toast.error(`Synopsis is ${total} words — trim to ${WORD_MAX} or below before saving.`);
      return;
    }
    setSaving(true);
    try {
      const md = toMarkdown(fields);
      const updated = await projectsApi.update(project.id, { synopsis: md });
      onSaved?.(updated);
      toast.success('Synopsis saved to the project.');
      window.dispatchEvent(new CustomEvent('loom:synopsis-saved'));
      return updated;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d
        : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join(' · ')
        : 'Could not save.';
      toast.error(msg);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => {
    const ok = await save();
    if (ok) onClose?.();
  };

  // iter55g24 — Save the synopsis first (if there are edits) then ask
  // Claude to break it into chapter transitions. Shows the resulting
  // beat list inline so the writer can then trigger "Draft Novel".
  const buildTransitions = async () => {
    if (!project?.id) return;
    if (total < WORD_MIN) {
      toast.error(`Synopsis must be at least ${WORD_MIN} words before we can turn it into chapters.`);
      return;
    }
    setTransitBusy(true);
    setTransitError('');
    try {
      // Persist current edits so the backend reads the latest text.
      const md = toMarkdown(fields);
      await projectsApi.update(project.id, { synopsis: md });
      const r = await axios.post(
        `${API}/projects/${project.id}/synopsis-to-transitions`,
        { chapters_count: project.chapters_count || undefined },
        { timeout: 120000 },
      );
      const rows = r.data?.transitions || [];
      setTransitions(rows);
      if (!rows.length) toast.error('Claude returned no transitions.');
      else toast.success(`Claude produced ${rows.length} chapter beats.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d
        : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join(' · ')
        : (e?.message || 'Transition build failed.');
      setTransitError(msg);
      toast.error(msg);
    } finally {
      setTransitBusy(false);
    }
  };

  // Kick off the existing "rebuild novel" flow, threading the freshly-
  // built transitions in as `user_guidance` so Claude drafts each
  // chapter around its assigned beat.
  const draftNovelFromTransitions = async () => {
    if (!transitions.length) return;
    if (!canBulkDraft) {
      const cta = upgradeCta('bulk_draft_novel');
      toast.info(cta.title, { description: cta.body, duration: 6000 });
      return;
    }
    const guidance = transitions
      .map((t) => `Chapter ${t.number} · ${t.title}: ${t.beat} (→ ${t.transition})`)
      .join('\n');
    onClose?.();
    toast.info('Drafting novel from beats — this can take several minutes.');
    await rebuildNovel({ guidance, includeExisting: false });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="flex max-h-[90vh] w-[min(95vw,52rem)] max-w-[52rem] flex-col overflow-hidden"
        data-testid="synopsis-builder-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Book className="h-4 w-4 text-primary" />
            Build synopsis
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Fill in the 11-step story arc below, or have Claude read your
            manuscript and draft it. Total length must be between
            {' '}{WORD_MIN}–{WORD_MAX} words.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 border-y border-panel-border bg-panel-header/40 px-2 py-1.5">
          <Button
            size="sm"
            onClick={build}
            disabled={busy || !project?.id}
            data-testid="synopsis-build-btn"
            className="gap-1"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {busy ? 'Reading manuscript…' : 'Have Claude build it'}
          </Button>
          <span
            className={`ml-auto rounded-sm px-2 py-0.5 text-[10.5px] font-mono-stat ${
              inRange ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
              : total > WORD_MAX ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
              : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
            }`}
            data-testid="synopsis-word-count"
          >
            {total.toLocaleString()} / {WORD_MIN}-{WORD_MAX} words
          </span>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-3">
          {STEPS.map((s) => (
            <div key={s.id} data-testid={`synopsis-step-${s.id}`}>
              <label className="mb-1 flex items-center gap-2 text-[11.5px] font-semibold">
                {s.label}
                <span className="font-normal text-muted-foreground text-[10.5px]">
                  · {countWords(fields[s.id] || '')} words
                </span>
              </label>
              <div className="mb-1 text-[10.5px] italic text-muted-foreground">{s.hint}</div>
              <textarea
                value={fields[s.id] || ''}
                onChange={(e) => patch(s.id, e.target.value)}
                data-testid={`synopsis-input-${s.id}`}
                rows={3}
                className="w-full resize-y rounded-sm border border-panel-border bg-background p-2 text-[12px] outline-none focus:border-primary"
                placeholder={s.hint}
              />
            </div>
          ))}

          {/* iter55g24 — Synopsis-to-chapter-transitions pipeline. */}
          {(transitions.length > 0 || transitError) && (
            <div
              className="mt-4 rounded-sm border border-panel-border bg-panel-header/40 p-3"
              data-testid="synopsis-transitions-section"
            >
              <div className="mb-2 flex items-center gap-2 text-[11.5px] font-semibold">
                <BookOpen className="h-3.5 w-3.5 text-primary" />
                Chapter transitions
                <span className="ml-auto font-normal text-muted-foreground text-[10.5px]">
                  {transitions.length} chapters
                </span>
              </div>
              {transitError && (
                <div className="rounded-sm border border-rose-500/40 bg-rose-500/10 p-2 text-[11px] text-rose-700 dark:text-rose-300">
                  {transitError}
                </div>
              )}
              {transitions.length > 0 && (
                <>
                  <ol className="space-y-2 pl-4 text-[11.5px] leading-snug">
                    {transitions.map((t) => (
                      <li key={t.number} data-testid={`transition-row-${t.number}`}>
                        <div className="font-semibold">
                          Ch {t.number}: {t.title}
                        </div>
                        {t.beat && (
                          <div className="text-[11px]">
                            <span className="text-muted-foreground">Beat:</span> {t.beat}
                          </div>
                        )}
                        {t.transition && (
                          <div className="text-[11px] italic">
                            <span className="text-muted-foreground">Hand-off:</span> {t.transition}
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={draftNovelFromTransitions}
                      data-testid="synopsis-draft-from-transitions-btn"
                      className="gap-1"
                      disabled={!canBulkDraft}
                      title={canBulkDraft ? 'Kick off bulk drafting using these beats as guidance.'
                        : upgradeCta('bulk_draft_novel')?.body || 'Available on Pro Author and above.'}
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      Draft Novel from these beats
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy || saving || transitBusy}>Cancel</Button>
          <Button
            variant="secondary"
            onClick={buildTransitions}
            disabled={busy || saving || transitBusy || total < WORD_MIN}
            data-testid="synopsis-to-transitions-btn"
            className="gap-1"
            title={total < WORD_MIN
              ? `Need at least ${WORD_MIN} words of synopsis before we can break it into chapters.`
              : 'Turn the 11-step synopsis into a chapter-by-chapter beat list.'}
          >
            {transitBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpen className="h-3.5 w-3.5" />}
            Convert to chapters
          </Button>
          <Button
            onClick={saveAndClose}
            disabled={busy || saving || total === 0}
            data-testid="synopsis-save-btn"
            className="gap-1"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save synopsis
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
